import { useState } from 'react';

import { Grid } from '@mui/material';

import useDashboardDownload from 'hooks/useDashboardDownload';
import { CuredApiPaths, DashboardNames, DownloadQueryKeys, ApiMethods } from 'models/enums';
import { CURATIONS_CSV_FILES } from 'pages/InsightsPage/dashboards/dashboardCsvFileNames';
import DashboardDownloadDialog from 'pages/InsightsPage/dashboards/DashboardDownloadDialog';
import DashboardDownloadSnackbar from 'pages/InsightsPage/dashboards/DashboardDownloadSnackbar';

import DashboardHeader from '../DashboardHeader';
import { sixMonthsAgo, today } from '../filtering/DateFilter/DateFilter';
import { FiltersForApiState, FilterValuesState } from '../filtering/types';
import { getTimeframeForApi } from '../filtering/utils';

import ClicksByDeviceCard from './cards/ClicksByDeviceCard';
import CurationStatsCards from './cards/CurationStatsCards';
import EngagementByCurationCard from './cards/EngagementByCurationCard';
import EngagementFunnel from './cards/EngagementFunnelCard';
import LinksByClickCard from './cards/LinksByClickCard';
import MessagesOverTimeCard from './cards/MessagesOverTimeCard';
import OpensByDeviceCard from './cards/OpensByDeviceCard';
import UniqueClicksCard from './cards/UniqueClicksCard';
import UniqueOpensCard from './cards/UniqueOpensCard';
import Filters from './Filters';

const CurationsDashboard = () => {
    const end = today;
    const start = sixMonthsAgo;
    // This state value is tracking the input values of the fields used for filtering.
    const [filterValues, setFilterValues] = useState<FilterValuesState>({
        includesUnsubscribes: false,
        selectedSendsValue: [],
        selectedCurationsValue: [],
        filterTimeframe: { start, end },
        filterRelativeTimeframe: {
            direction: null,
            duration: null,
            unit: null,
        },
    });
    const [isRelativeTimeframe, setIsRelativeTimeframe] = useState(false);

    const sendsForApi = filterValues.selectedSendsValue?.map((send) => send?.send_id);
    const campaignsForApi = filterValues.selectedCurationsValue?.map((campaign) => campaign?.campaign_id);
    const timeframeForApi = getTimeframeForApi(isRelativeTimeframe, filterValues);

    // This state value is tracking what's sent to the API for filtering, which may not
    // match the field values as filters are not applied until a button is clicked.
    const [filtersForApi, setFiltersForApi] = useState<FiltersForApiState>({
        unsubs: filterValues.includesUnsubscribes,
        sends: sendsForApi,
        campaigns: campaignsForApi,
        timeframe: timeframeForApi,
    });

    const {
        handleDownloadClick,
        isDialogOpen,
        onDialogClose,
        onDownloadAll,
        onDownloadFile,
        isDownloading,
        currentFilename,
        isSuccess,
        error,
        resetDownloadState,
        csvFilenames,
    } = useDashboardDownload({
        queryKey: DownloadQueryKeys.CURATIONS_DASHBOARD,
        path: CuredApiPaths.REPORT_CURATION_DOWNLOAD,
        requestMethod: ApiMethods.POST,
        csvFilenames: CURATIONS_CSV_FILES,
        filtersForApi,
    });

    // This handles clicking on the button to apply filters by setting the filtersForApi
    // state to match the input state values.
    const handleApplyFilters = () => {
        setFiltersForApi({
            unsubs: filterValues.includesUnsubscribes,
            sends: sendsForApi,
            campaigns: campaignsForApi,
            timeframe: timeframeForApi,
        });
    };

    return (
        <div style={{ paddingBottom: '32px' }}>
            <DashboardDownloadSnackbar
                isDownloading={isDownloading}
                isSuccess={isSuccess}
                error={error}
                dashboardName={DashboardNames.CURATIONS}
                onSuccessClosed={resetDownloadState}
                onErrorClosed={resetDownloadState}
            />
            <DashboardDownloadDialog
                isOpen={isDialogOpen}
                onClose={onDialogClose}
                onDownloadAll={onDownloadAll}
                onDownloadFile={onDownloadFile}
                filenames={csvFilenames}
                isDownloading={isDownloading}
                currentFilename={currentFilename}
            />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Filters
                        filterValues={filterValues}
                        handleApplyFilters={handleApplyFilters}
                        isRelativeTimeframe={isRelativeTimeframe}
                        setFilterValues={setFilterValues}
                        setIsRelativeTimeframe={setIsRelativeTimeframe}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <DashboardHeader
                        title="email"
                        subtitle="take action with cured insights"
                        description="Filter on specific email Curations to quickly identify what's working to improve future sends."
                        handleDownloadClick={handleDownloadClick}
                    />
                </Grid>
                <CurationStatsCards filtersForApi={filtersForApi} />
                <Grid item xs={12} md={8}>
                    <MessagesOverTimeCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <EngagementFunnel filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12} md={8}>
                    <UniqueClicksCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <OpensByDeviceCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12} md={8}>
                    <UniqueOpensCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ClicksByDeviceCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12}>
                    <EngagementByCurationCard filtersForApi={filtersForApi} />
                </Grid>
                <Grid item xs={12}>
                    <LinksByClickCard filtersForApi={filtersForApi} />
                </Grid>
            </Grid>
        </div>
    );
};

export default CurationsDashboard;
